import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Share2, MessageCircle, Globe, Copy, Check, Loader2, Search, Send, X } from "lucide-react";
import { createPost, sendMessage, searchUsers } from "@/lib/api";
import { toast } from "sonner";

/**
 * ShareButton — reusable share component for listings, businesses, shops, etc.
 * 
 * Props:
 *   title: Name of the item being shared
 *   description: Short description
 *   url: The URL to share (relative path like /directory/abc123)
 *   image: Optional image URL
 *   type: "business" | "listing" | "shop" | "job"
 *   size: "sm" | "default"
 *   variant: "outline" | "ghost"
 */
export function ShareButton({ title, description, url, image, type = "business", size = "sm", variant = "outline", className = "" }) {
  const [open, setOpen] = useState(false);
  const fullUrl = url?.startsWith("http") ? url : `${window.location.origin}${url}`;

  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOpen(true)}
        className={`${className}`} title="Share" data-testid="share-btn">
        <Share2 className="w-4 h-4" />
      </Button>
      {open && (
        <ShareDialog
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          description={description}
          url={fullUrl}
          image={image}
          type={type}
        />
      )}
    </>
  );
}

export function ShareDialog({ open, onClose, title, description, url, image, type }) {
  const [mode, setMode] = useState(null); // null = menu, "feed" = post to feed, "message" = send DM
  const [feedText, setFeedText] = useState("");
  const [posting, setPosting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Message mode
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  const typeLabel = type === "business" ? "business" : type === "listing" ? "listing" : type === "shop" ? "shop" : type === "job" ? "job" : "item";

  const handleShareExternal = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description || title, url });
        onClose();
      } catch {}
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handlePostToFeed = async () => {
    setPosting(true);
    try {
      const content = feedText.trim()
        ? `${feedText.trim()}\n\n${url}`
        : `Check out this ${typeLabel}: ${title}\n\n${url}`;
      await createPost({ content, scope: "global" });
      toast.success("Shared to feed!");
      onClose();
    } catch {
      toast.error("Failed to post");
    }
    setPosting(false);
  };

  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await searchUsers(searchQuery.trim(), { limit: 8 });
      setSearchResults(res.data?.users || res.data || []);
    } catch {}
    setSearching(false);
  };

  const handleSendMessage = async () => {
    if (!selectedUser) return;
    setSending(true);
    try {
      const content = messageText.trim()
        ? `${messageText.trim()}\n\n${title}: ${url}`
        : `Check out this ${typeLabel}: ${title}\n${url}`;
      await sendMessage({ receiver_id: selectedUser.user_id, content });
      toast.success(`Sent to ${selectedUser.name}`);
      onClose();
    } catch {
      toast.error("Failed to send");
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm bg-white">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-lg">Share {title}</DialogTitle>
        </DialogHeader>

        {!mode ? (
          /* Share menu */
          <div className="space-y-2">
            <button onClick={() => setMode("feed")}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#F9F7F2] transition-colors text-left">
              <div className="w-10 h-10 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-[var(--liturgical-primary)]" />
              </div>
              <div>
                <p className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">Share to Feed</p>
                <p className="text-xs text-[#6B665E] font-['Nunito']">Post to your feed for everyone to see</p>
              </div>
            </button>
            <button onClick={() => setMode("message")}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#F9F7F2] transition-colors text-left">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">Send as Message</p>
                <p className="text-xs text-[#6B665E] font-['Nunito']">Send directly to a friend</p>
              </div>
            </button>
            <button onClick={handleShareExternal}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#F9F7F2] transition-colors text-left">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">Share Externally</p>
                <p className="text-xs text-[#6B665E] font-['Nunito']">Via SMS, email, or other apps</p>
              </div>
            </button>
            <button onClick={handleCopyLink}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#F9F7F2] transition-colors text-left">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
              </div>
              <div>
                <p className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">{copied ? "Copied!" : "Copy Link"}</p>
                <p className="text-xs text-[#6B665E] font-['Nunito'] truncate max-w-[200px]">{url}</p>
              </div>
            </button>
          </div>
        ) : mode === "feed" ? (
          /* Post to feed */
          <div className="space-y-3">
            <button onClick={() => setMode(null)} className="text-xs text-[#6B665E] font-['Nunito'] hover:text-[#2D2A26]">Back</button>
            <Textarea value={feedText} onChange={e => setFeedText(e.target.value)}
              placeholder={`Say something about this ${typeLabel}... (optional)`}
              className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[60px]" />
            <div className="bg-[#F9F7F2] rounded-lg p-3 border border-[#E5E0D5]">
              <p className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] truncate">{title}</p>
              {description && <p className="text-xs text-[#6B665E] font-['Nunito'] truncate mt-0.5">{description}</p>}
            </div>
            <Button onClick={handlePostToFeed} disabled={posting} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
              {posting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Post to Feed
            </Button>
          </div>
        ) : (
          /* Send as message */
          <div className="space-y-3">
            <button onClick={() => setMode(null)} className="text-xs text-[#6B665E] font-['Nunito'] hover:text-[#2D2A26]">Back</button>
            {!selectedUser ? (
              <>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                    <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleSearchUsers()}
                      placeholder="Search by name..." className="pl-9 border-[#E5E0D5] font-['Nunito'] text-sm" />
                  </div>
                  <Button onClick={handleSearchUsers} disabled={searching} size="sm" className="bg-[var(--liturgical-primary)] text-white">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                  </Button>
                </div>
                {searchResults.length > 0 && (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {searchResults.map(u => (
                      <button key={u.user_id} onClick={() => setSelectedUser(u)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-[#F9F7F2] text-left">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{u.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <span className="font-['Nunito'] text-sm">{u.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 bg-[#F9F7F2] rounded-lg px-3 py-2">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={selectedUser.picture} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{selectedUser.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <span className="font-['Nunito'] text-sm font-medium flex-1">{selectedUser.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)} className="h-6 w-6 p-0"><X className="w-3.5 h-3.5" /></Button>
                </div>
                <Textarea value={messageText} onChange={e => setMessageText(e.target.value)}
                  placeholder="Add a message... (optional)" className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[50px]" />
                <Button onClick={handleSendMessage} disabled={sending} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Message
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
